import { useMaybeAssignCategoryTreeExperimentForFilterSection } from "@faire/deprecated-retailer-visitor-shared/components/Filter/useMaybeAssignCategoryTreeExperimentForFilterSection";
import { getSearchInputPlaceholder } from "@faire/deprecated-retailer-visitor-shared/components/FilterPills/consts";
import { FilterOption } from "@faire/deprecated-retailer-visitor-shared/components/Filters/__internal__/FilterOption";
import {
  clearFilterSection,
  DEFAULT_OPTION_GROUP_NAME,
  getFilteredOptionsGroupMap,
  getSelectedOptions,
  isDefaultShowAllFilterSection,
  SEE_MORE_STEP,
  SEE_MORE_THRESHOLD,
} from "@faire/deprecated-retailer-visitor-shared/components/Filters/FilterUtils";
import { assignSettingOnEvent } from "@faire/deprecated-retailer-visitor-shared/settings/assignSettingOnEvent";
import { useRecordMilestone } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useMilestone";
import { Core } from "@faire/design-tokens";
import { trackRetailersFilterInteraction } from "@faire/web-api--source/events/retailers/actionCategoryOther/filterInteraction";
import { trackRetailersFilterClearClick } from "@faire/web-api--source/events/retailers/click/filterClear";
import { IFilterOption } from "@faire/web-api--source/indigofair/data/IFilterOption";
import { IFilterSection } from "@faire/web-api--source/indigofair/data/IFilterSection";
import type { IUserMilestone } from "@faire/web-api--source/indigofair/data/IUserMilestone";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Badge as SlateBadge } from "@faire/web/slate-deprecated/Badge";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { SearchInput } from "@faire/web/slate/SearchInput";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect, useRef, useCallback } from "react";
import styled from "styled-components";

interface IProps {
  name: string;
  section: IFilterSection;
  onChange: (
    section: IFilterSection,
    filterKey: string,
    value: boolean
  ) => void;
  initiallyCollapsed?: boolean;
  className?: string;
  collapsable?: boolean;
  showAll?: boolean;
  showLoading?: boolean;
  showUnformattedResultsCount?: boolean;
  divRef?: React.RefObject<HTMLDivElement | null>;
}

export const FilterSection: React.FC<IProps> = ({
  name,
  section,
  initiallyCollapsed = true,
  collapsable = true,
  onChange,
  className,
  showAll,
  showLoading,
  showUnformattedResultsCount = false,
  divRef,
}) => {
  // Used for functions that need sections reference and are unstable with sections inside the useCallback dependency
  const sectionRef = useRef(section);
  sectionRef.current = section;
  const { strictLocalize } = useStrictLocalization();
  const isInvisible = section.is_invisible ?? false;

  const [visibleOptionCount, setVisibleOptionCount] =
    React.useState(SEE_MORE_THRESHOLD);
  const maybeAssignCategoryTreeExperimentForFilterSection =
    useMaybeAssignCategoryTreeExperimentForFilterSection();

  useEffect(() => {
    if (showAll) {
      setVisibleOptionCount(SEE_MORE_STEP + SEE_MORE_THRESHOLD);
    }
  }, [showAll]);

  const [searchValue, setSearchValue] = React.useState("");

  const recordMilestone = useRecordMilestone();
  const isDefaultShowAllSection: boolean =
    isDefaultShowAllFilterSection(section);

  const handleMilestone = useCallback(
    (milestone: IUserMilestone | undefined) => {
      if (!milestone || !milestone.retailer_milestone) {
        return;
      }
      recordMilestone(milestone.retailer_milestone);
    },
    [recordMilestone]
  );

  const optionsGroupMapResults = React.useMemo(
    () =>
      getFilteredOptionsGroupMap(
        section,
        searchValue,
        !isDefaultShowAllSection ? visibleOptionCount : undefined
      ),
    [searchValue, section, isDefaultShowAllSection, visibleOptionCount]
  );

  const selectedOptions = getSelectedOptions(section);

  const [collapsed, setCollapsed] = React.useState(initiallyCollapsed);

  const viewRef = useDoActionWhenVisible(() => {
    maybeAssignCategoryTreeExperimentForFilterSection(
      section,
      section.options.slice(0, visibleOptionCount).map((option) => option.key)
    );
  });

  const settingAssignmentRef = useDoActionWhenVisible(() => {
    section.settings_to_assign?.forEach((setting) => {
      assignSettingOnEvent(setting);
    });
  });

  const handleSubCategoryExpandEvent = useCallback(
    (filterOption: IFilterOption) => {
      const section = sectionRef.current;
      maybeAssignCategoryTreeExperimentForFilterSection(
        section,
        filterOption.sub_options.map((option) => option.key)
      );
    },
    [maybeAssignCategoryTreeExperimentForFilterSection]
  );

  const handleHeaderClick = useCallback(() => {
    if (!collapsable) {
      return;
    }
    trackRetailersFilterInteraction(
      name,
      collapsed ? "expand_section" : "collapse_section",
      section.field_name ?? ""
    );
    setCollapsed(!collapsed);
  }, [collapsable, collapsed, name, section.field_name]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(event.target.value);
    },
    []
  );

  const handleSearchClear = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleChange = useCallback(
    (section: IFilterSection, filterKey: string, value: boolean) => {
      handleMilestone(section.badge?.dismiss_milestone);
      onChange(section, filterKey, value);
    },
    [handleMilestone, onChange]
  );

  const handleSectionClear = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const section = sectionRef.current;
      event.stopPropagation();
      trackRetailersFilterClearClick(
        name,
        "section_clear",
        section.field_name ?? ""
      );
      clearFilterSection(section);
      handleChange(section, "", false);
    },
    [handleChange, name]
  );

  const handleShowSeeMore = useCallback(() => {
    const section = sectionRef.current;
    setVisibleOptionCount(visibleOptionCount + SEE_MORE_STEP);
    maybeAssignCategoryTreeExperimentForFilterSection(
      section,
      section.options.map((option) => option.key)
    );
  }, [maybeAssignCategoryTreeExperimentForFilterSection, visibleOptionCount]);

  const handleShowSeeLess = useCallback(() => {
    setVisibleOptionCount(SEE_MORE_THRESHOLD);
  }, []);

  const renderOption = useCallback(
    (option: IFilterOption, isSubOption: boolean) => {
      if (!option.key) {
        return null;
      }

      const section = sectionRef.current;

      return (
        <FilterOption
          key={`option-${option.key}`}
          isSubOption={isSubOption}
          section={section}
          option={option}
          searchValue={searchValue}
          onChange={handleChange}
          renderOption={renderOption}
          onExpand={handleSubCategoryExpandEvent}
          showUnformattedResultsCount={showUnformattedResultsCount}
        />
      );
    },
    [
      handleChange,
      handleSubCategoryExpandEvent,
      searchValue,
      showUnformattedResultsCount,
    ]
  );

  const renderOptionGroup = useCallback(
    (groupName: string, options: IFilterOption[]) => {
      return (
        <React.Fragment key={`option-groups-${groupName}`}>
          {groupName !== DEFAULT_OPTION_GROUP_NAME ? (
            <FilterGroupTitle variant="paragraphSansMedium">
              {groupName}
            </FilterGroupTitle>
          ) : null}
          <OptionsWrapper>
            {options.map((option) => renderOption(option, false))}
          </OptionsWrapper>
        </React.Fragment>
      );
    },
    [renderOption]
  );

  const renderSeeMore = () => {
    if (
      optionsGroupMapResults.totalFilteredOptions <= SEE_MORE_THRESHOLD ||
      isDefaultShowAllSection
    ) {
      return <Spacer height="1x" />;
    }
    return (
      <ButtonWrapper>
        {section.options.length > visibleOptionCount ? (
          <Button variant="tertiary" onClick={handleShowSeeMore}>
            <StrictLocalMsg
              id="3Uop6y"
              defaultMessage="Show more"
              description={{ text: "Expand a list of filter options" }}
            />
          </Button>
        ) : (
          <Button variant="tertiary" onClick={handleShowSeeLess}>
            <StrictLocalMsg
              id="sCPzsF"
              defaultMessage="Show less"
              description={{ text: "Shrink a list of filter options" }}
            />
          </Button>
        )}
      </ButtonWrapper>
    );
  };

  const renderCollapsedSection = () => {
    if (showLoading) {
      return;
    }
    return <SelectedOptionsText selectedOptions={selectedOptions} />;
  };

  if (!showLoading && !optionsGroupMapResults.optionsGroupMap) {
    return null;
  }

  return (
    <div
      ref={settingAssignmentRef}
      data-test-id={`filter-section-impression-wrapper-${section.display_name}`}
    >
      <Wrapper
        className={`${className ?? ""} ${isInvisible ? "hidden" : ""}`}
        ref={divRef}
        data-test-id={`filter-section-wrapper-${section.display_name}`}
      >
        <Header
          role="heading"
          aria-level={3}
          aria-label={
            section.display_name ??
            strictLocalize({
              id: "val32h",
              defaultMessage: "Loading",
              description: {
                text: "Waiting for the data to be loaded",
              },
            })
          }
          aria-expanded={!collapsed}
          aria-disabled={!collapsable}
          aria-controls={`filter-section-options-${section.field_name}`}
          id={`filter-section-header-${section.field_name}`}
          onClick={handleHeaderClick}
          $collapsable={collapsable}
        >
          <Column>
            {showLoading ? (
              <LoadingTextPlaceholder />
            ) : (
              <Row>
                <Badge section={section} />
                <StyledHeaderText variant="paragraphSansMedium">
                  {section.display_name}
                </StyledHeaderText>
              </Row>
            )}
          </Column>

          {selectedOptions.length > 0 &&
          section.field_function !== IFilterSection.FieldFunction.SORT ? (
            <ClearAllColumn>
              <Button variant="tertiary" onClick={handleSectionClear}>
                <StrictLocalMsg
                  id="Z7s5LH"
                  defaultMessage="Clear"
                  description={{
                    text: "Shortcut button to clear filters",
                  }}
                />
              </Button>
            </ClearAllColumn>
          ) : null}
        </Header>
        <section
          id={`filter-section-options-${section.field_name}`}
          aria-labelledby={`filter-section-header-${section.field_name}`}
          hidden={collapsed}
          ref={viewRef}
        >
          {!collapsed ? (
            <>
              <FilterSectionScrollable
                role="list"
                aria-label={`${section.display_name}`}
              >
                {section.searchable ? (
                  <>
                    <SearchInput
                      onChange={handleSearchChange}
                      onClear={handleSearchClear}
                      placeholder={getSearchInputPlaceholder(
                        section.search_label_text,
                        section.display_name,
                        strictLocalize
                      )}
                      value={searchValue}
                      className="search-filter-field"
                      data-test-id={`filter-search-field-${section.field_name}`}
                    />
                    <Spacer height="3x" />
                  </>
                ) : null}
                {Object.entries(optionsGroupMapResults.optionsGroupMap).map(
                  ([groupName, filterOptions]) =>
                    renderOptionGroup(groupName, filterOptions)
                )}
                {renderSeeMore()}
                {searchValue !== "" &&
                optionsGroupMapResults.totalFilteredOptions === 0 ? (
                  <Typography
                    variant="paragraphSansRegular"
                    color={Core.text.subdued}
                  >
                    <StrictLocalMsg
                      id="kqbRcv"
                      defaultMessage="No results"
                      description={{
                        text: "Title when search has no results",
                        img: "https://cdn.faire.com/fastly/a7621827c0e60305f51daa9f66b2aa0d7716a28adaa399abb1d88dca028783bd.png",
                      }}
                    />
                  </Typography>
                ) : null}
              </FilterSectionScrollable>
              <Spacer height="2x" />
            </>
          ) : null}
        </section>
        {collapsed ? renderCollapsedSection() : null}
      </Wrapper>
    </div>
  );
};

const Badge: React.FC<{ section: IFilterSection }> = ({ section }) => {
  const { strictLocalize } = useStrictLocalization();
  if (!section.badge || section.badge.badge_type !== "NEW_BADGE") {
    return null;
  }
  return (
    <>
      <SlateBadge
        backgroundColor={Core.surface.success}
        content={strictLocalize({
          id: "94ABFq",
          defaultMessage: "New",
          description: {
            text: "New feature",
            img: "https://cdn.faire.com/fastly/88f20d6b86e42faa58f0c1da3dcbc255acc199c68cd9d3d73a3928ce12deaf57.png",
          },
        })}
      />
      <Spacer width="1x" />
    </>
  );
};

const SelectedOptionsText: React.FC<{ selectedOptions: IFilterOption[] }> = ({
  selectedOptions,
}) => {
  const maxNumberOfOptionsToShow = 4;
  const numberOfMore = selectedOptions.length - maxNumberOfOptionsToShow;
  if (selectedOptions.length < 1) {
    return null;
  }
  const names = selectedOptions
    .slice(0, maxNumberOfOptionsToShow)
    .map((option) => option.display_name)
    .join(", ");
  return (
    <>
      <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
        {names}
        {numberOfMore > 0 ? (
          <>
            {" "}
            <StrictLocalMsg
              id="3XuKb9"
              defaultMessage=" + {moreOptionsNumber} more"
              description={{
                text: "Text to show how many more filters selected but not displayed",
                img: "https://cdn.faire.com/fastly/f9f23bdc9eba3bd492d13593816654f4929e34e2e9e8ad7a749a2ddd561b816a.png",
              }}
              values={{
                moreOptionsNumber: numberOfMore,
              }}
            />
          </>
        ) : null}
      </Typography>
      <Spacer height="3x" />
    </>
  );
};

const Wrapper = styled.div`
  width: 100%;
`;

const Header = styled(Row)<{ $collapsable?: boolean }>`
  cursor: ${(props) => (props.$collapsable ? "pointer" : "unset")};
  padding: ${getSpacing("3x", 0)};
`;

const ClearAllColumn = styled(Column)`
  margin-left: auto;
  margin-right: ${getSpacing("2x")};
`;

const FilterGroupTitle = styled(Typography)`
  &&& {
    margin-top: ${getSpacing("3x")};
    margin-bottom: ${getSpacing("2x")};
    ${responsiveMediaQueries.mobile} {
      margin-top: ${getSpacing("4x")};
    }
  }
`;

const OptionsWrapper = styled(Row).attrs({ align: "center" })`
  position: relative;
  width: 100%;
  flex-wrap: wrap;
`;

const ButtonWrapper = styled(Row).attrs({ align: "center" })`
  position: relative;
  width: 100%;
  flex-wrap: wrap;
  padding-bottom: ${getSpacing("1x")};
`;

const FilterSectionScrollable = styled(Column)`
  position: relative;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  padding-right: ${getSpacing("2x")};
`;

const LoadingTextPlaceholder = styled.div`
  height: 16px;
  width: 129px;
  background-color: ${Core.surface.subdued};
`;

const StyledHeaderText = styled(Typography)`
  &&& {
    -ms-word-break: break-word;
    word-break: break-word;
    white-space: pre-wrap;
    padding-right: ${getSpacing("1x")};
  }
`;
